import type { CatalogProduct } from '@aibees/shared/definition';

/**
 * Cells and productCounts on a grouped catalog are keyed by the family (e.g.
 * "starter") for paid plans and by the product name (e.g. "Free") for Free.
 * Correlate via `family ?? name`, not the raw product id.
 *
 * Use this only for the FE's own internal value-maps (where the key just has
 * to be stable and unique per column). To read a column out of a feature's
 * `cells` / `products`, use `matchColumnKey` instead — the backend's casing
 * for those keys isn't guaranteed to match the product metadata.
 */
export function getColumnKey(product: CatalogProduct): string {
  return product.metadata.family ?? product.name;
}

/**
 * Resolve the key a product uses inside a feature's `cells` / `products`
 * arrays, matching case-insensitively against the product's `family` then
 * `name`.
 *
 * The grouped catalog's column keys don't reliably match the casing (or the
 * family-vs-name choice) of the product metadata — e.g. keys can arrive as
 * lowercase "starter" while `product.name` is "Starter", or vice versa. An
 * exact `family ?? name` lookup then misses for the paid plans and every row
 * renders as a disabled/greyed feature. Matching against the keys actually
 * present fixes that without risking a false match, since the family/name of
 * the four columns are distinct.
 */
export function matchColumnKey(
  product: CatalogProduct,
  keys: string[]
): string | undefined {
  const candidates = [product.metadata.family, product.name].filter(
    (candidate): candidate is string => !!candidate
  );

  for (const candidate of candidates) {
    const hit = keys.find(
      (key) => key.toLowerCase() === candidate.toLowerCase()
    );
    if (hit) return hit;
  }

  return undefined;
}
